import { CONSTANTS, TILE_COUNT } from "./constants";
import { DICE, validateDice } from "./dice";
import { mulberry32, shuffle } from "./rng";
import type { Path, TileIndex } from "./types";

/**
 * The letters a tile contributes to a word. The Q die face is always the
 * digraph "QU" (§1.1), which is why word length is measured on the assembled
 * string and never on the path length.
 */
export function faceLetters(face: string): string {
  return face === "Q" ? "QU" : face;
}

/** Display form of a tile face — "Qu" reads better than "QU" on a tile. */
export function faceDisplay(face: string): string {
  return face === "Q" ? "Qu" : face;
}

/** Row-major neighbours, 8-way, precomputed once for the fixed grid. */
export const NEIGHBOURS: readonly (readonly TileIndex[])[] = (() => {
  const g = CONSTANTS.GRID;
  const out: TileIndex[][] = [];
  for (let i = 0; i < TILE_COUNT; i += 1) {
    const r = Math.floor(i / g);
    const c = i % g;
    const near: TileIndex[] = [];
    for (let dr = -1; dr <= 1; dr += 1) {
      for (let dc = -1; dc <= 1; dc += 1) {
        if (dr === 0 && dc === 0) continue;
        const nr = r + dr;
        const nc = c + dc;
        if (nr < 0 || nr >= g || nc < 0 || nc >= g) continue;
        near.push(nr * g + nc);
      }
    }
    out.push(near);
  }
  return out;
})();

export function areAdjacent(a: TileIndex, b: TileIndex): boolean {
  return NEIGHBOURS[a].includes(b);
}

/**
 * Whether a path is legal: in bounds, every step adjacent, no tile reused within
 * the single word (§1.2). Reuse ACROSS words is not only legal but the whole
 * point of heat.
 */
export function isPathValid(path: Path): boolean {
  if (path.length === 0) return false;
  const seen = new Set<TileIndex>();
  for (let i = 0; i < path.length; i += 1) {
    const t = path[i];
    if (!Number.isInteger(t) || t < 0 || t >= TILE_COUNT) return false;
    if (seen.has(t)) return false;
    seen.add(t);
    if (i > 0 && !areAdjacent(path[i - 1], t)) return false;
  }
  return true;
}

/** Assemble the fired word from a path. Uppercase; Q contributes "QU". */
export function wordFromPath(board: string, path: Path): string {
  let out = "";
  for (const t of path) out += faceLetters(board[t]);
  return out;
}

/**
 * Roll a board from a seed (§3.1 steps 1-2): shuffle the 25 dice, then take one
 * face from each. The die order after the shuffle is the tile order, so both the
 * shuffle and the face draw consume the same stream in a fixed sequence.
 */
export function rollBoard(seed: number): { board: string; gold: TileIndex } {
  validateDice();
  const rand = mulberry32(seed);
  const order = shuffle([...DICE], rand);
  let board = "";
  for (const die of order) {
    board += die[Math.floor(rand() * die.length)];
  }
  const gold = Math.floor(rand() * TILE_COUNT);
  return { board, gold };
}

/** Structural check on a board string from untrusted JSON. */
export function validateBoard(board: string, gold: TileIndex): void {
  if (board.length !== TILE_COUNT) {
    throw new Error(`board: expected ${TILE_COUNT} chars, got ${board.length}`);
  }
  if (!/^[A-Z]+$/.test(board)) throw new Error("board: must be A-Z uppercase");
  if (!Number.isInteger(gold) || gold < 0 || gold >= TILE_COUNT) {
    throw new Error(`board: gold index ${gold} out of range`);
  }
}
